// @flow
import React, { Fragment, Node } from 'react';
import { get, isEmpty } from 'lodash';

import { RESORT_DESTINATION } from 'Client/cms/toolkit/constants';
import ratioUtils from 'Components/utils/ratios/ratios.utils';
import { ICONS } from 'Components/utils/icon/icons';

import { ResponsiveTabs } from 'Components/frameworks/responsive-tabs/ResponsiveTabs';
import { Text } from 'Components/frameworks/text/Text';
import { Title } from 'Components/frameworks/title/Title';
import Image from 'Components/frameworks/image/Image';
import { type IconName } from 'Components/frameworks/icon/Icon';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';

import {
  productDestinationResolver,
  type GQLProps,
  type CMSProps,
  type PracticalInformation,
} from './productDestination.resolver';

type LineProps = CMSProps & GQLProps;

type TabItem = {
  header: {
    iconName: IconName,
    title: string,
  },
  content: Array<?Node>,
};

type Titles = {
  healthFormalities: string,
  passportAndVisas: string,
};

function renderArea(props: LineProps) {
  const {
    resort: { name: title = '', description = '', productVideo, strengths = [] },
  } = props;

  return (
    <div className="sm:flex">
      <Title className="sm:w-1/4" theme="title-sans-2">
        {title}
      </Title>
      <div className="sm:w-3/4">
        <Text text={description} />
        {description ? (
          <hr className="block border-0 border-solid border-gray-light border-t-1 h-px my-4 mx-0 p-0" />
        ) : null}
        <div className="sm:flex">
          <div className="mb-5 sm:rtl:mr-5 sm:mb-0 sm:w-1/3 image-responsive-cell">
            <Image imageUrl={productVideo} ratioKey={ratioUtils.XL} />
          </div>
          {!isEmpty(strengths) ? (
            <ul data-testid="Destination-strengths" className="reset-list text-base sm:w-2/3">
              {strengths.map(
                (strength, index): Node => {
                  return (
                    <li className="list-disc" key={index}>
                      {strength}
                    </li>
                  );
                },
              )}
            </ul>
          ) : null}
        </div>
      </div>
    </div>
  );
}

function renderResponsiveTabsContent(content, id) {
  return content ? <Text key={id} className="py-5" text={content} /> : null;
}

function getResponsiveTabsItems(
  titles: Titles,
  practicalInformation: PracticalInformation,
): Array<TabItem> {
  const { healthFormalities = '', passportAndVisas = '' } = titles;

  return [
    {
      header: {
        iconName: ICONS.HEALTH,
        title: healthFormalities,
      },
      content: [renderResponsiveTabsContent(practicalInformation.healthFormalities, 'health')],
    },
    {
      header: {
        iconName: ICONS.PASSPORT,
        title: passportAndVisas,
      },
      content: [renderResponsiveTabsContent(practicalInformation.passportAndVisas, 'passport')],
    },
  ];
}

function hasResponsiveTabs(props: LineProps) {
  return (
    get(props, 'resort.practicalInformation.healthFormalities', null) ||
    get(props, 'resort.practicalInformation.passportAndVisas', null)
  );
}

const ProductDestinationsLine = (props: LineProps) => {
  const { healthFormalities, passportAndVisas, resort } = props;
  const { strengths = [] } = resort;
  const shouldRenderArea = strengths.length > 0 || !!resort.productVideo;
  const description = get(resort, 'destination.description', '');
  return (
    <SiteContainer className="Line">
      <div className="pt-10" data-testid="ProductInformationDestination">
        <div className="sm:flex">
          <Title className="sm:w-1/4 mb-5" theme="title-sans-2">
            {get(props, 'resort.destination.country', '')}
          </Title>
          <div className="sm:w-3/4">
            <Text text={description} />
            {hasResponsiveTabs(props) ? (
              <Fragment>
                <ResponsiveTabs
                  areAllCollapsable
                  className="mt-5"
                  expandIconName={ICONS.PLUS}
                  items={getResponsiveTabsItems(
                    { healthFormalities, passportAndVisas },
                    get(props, 'resort.practicalInformation'),
                  )}
                />
                <hr className="block border-0 border-solid border-gray-light border-t-1 h-px my-4 mx-0 p-0" />
              </Fragment>
            ) : null}
          </div>
        </div>
        {shouldRenderArea ? renderArea(props) : null}
      </div>
    </SiteContainer>
  );
};

export const productDestinationDescriptor = {
  name: RESORT_DESTINATION,
  component: ProductDestinationsLine,
  resolve: productDestinationResolver.resolve,
};
